import * as React from "react";
import styled from "styled-components";
import { Button, Classes, Dialog, Icon } from "@blueprintjs/core";
import classNames from "classnames";
import { useDatatable2Context } from "../DataTable2";
import { ColumnCard } from "./ColumnCard";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import sortBy from "lodash/sortBy";
import { IDataTableColumn } from "./IDataTableColumn";

export interface IColumnSelectorProps {}

const Container = styled.div``;

const FooterActionsContainer = styled.div`
  justify-content: space-between !important;
`;

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  & > * + * {
    margin-top: 0.5rem;
  }
`;

export const ColumnSelector: React.FunctionComponent<
  IColumnSelectorProps
> = () => {
  const {
    translateFunc,
    resetColumnsState,
    setColumnOrder,
    columns,
    isColumnVisible,
    tableState,
  } = useDatatable2Context();

  const customizableColumns = React.useMemo(
    () => columns.filter((c) => !c.disableCustomization),
    [columns]
  );

  const [selectionPopupOpened, setSelectionPopupOpened] = React.useState(false);
  const [orderedCols, setOrderedCols] = React.useState<IDataTableColumn<any>[]>(
    []
  );

  const onEditVisibleButtonClick = React.useCallback(() => {
    setSelectionPopupOpened(true);
  }, []);

  const onSelectionPopupClose = React.useCallback(() => {
    setSelectionPopupOpened(false);
  }, []);

  const moveCard = React.useCallback(
    (index: number, toIndex: number) => {
      const cards = [...orderedCols];
      const found = cards[index];
      cards.splice(index, 1);
      cards.splice(toIndex, 0, found);
      setOrderedCols(cards);
    },
    [orderedCols]
  );

  const saveColumnsState = React.useCallback(() => {
    setColumnOrder(orderedCols);
    onSelectionPopupClose();
  }, [onSelectionPopupClose, orderedCols, setColumnOrder]);

  const resetColumnOrder = React.useCallback(() => {
    setOrderedCols(customizableColumns.filter((c) => !c.fixed));
    resetColumnsState();
  }, [customizableColumns, resetColumnsState]);

  React.useEffect(() => {
    if (customizableColumns) {
      const sorted = sortBy(tableState.columnCustomizations, (d) => d.position)
        .map((d) =>
          customizableColumns.find((c) => c.fieldName === d.fieldName)
        )
        .filter((d) => !!d)
        .filter((c) => !c.fixed);
      setOrderedCols(sorted);
    }
  }, [customizableColumns, setColumnOrder, tableState.columnCustomizations]);

  const visibleColumnsCount = React.useMemo(
    () => orderedCols.filter((col) => isColumnVisible(col))?.length ?? 0,
    [isColumnVisible, orderedCols]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <Container>
        <Button
          icon="th"
          minimal
          onClick={onEditVisibleButtonClick}
          title={translateFunc("ColumnSelector_EditVisibleColumns")}
        ></Button>
        <Dialog
          isOpen={selectionPopupOpened}
          hasBackdrop
          canEscapeKeyClose={true}
          canOutsideClickClose={true}
          onClose={onSelectionPopupClose}
        >
          <div className={classNames(Classes.DIALOG_HEADER)}>
            <Icon icon="th" iconSize={24}></Icon>
            <h4 className={Classes.HEADING}>
              {translateFunc("ColumnSelector_EditVisibleColumn")}
            </h4>
            <Button
              aria-label={translateFunc("ColumnSelector_Close")}
              minimal
              icon="cross"
              className={Classes.DIALOG_CLOSE_BUTTON}
              onClick={onSelectionPopupClose}
            ></Button>
          </div>
          <div className={Classes.DIALOG_BODY}>
            <CheckBoxesContainer>
              {orderedCols.map(
                (col, i) =>
                  col && (
                    <ColumnCard
                      col={col}
                      moveCard={moveCard}
                      key={`col_${col.fieldName}`}
                      index={i}
                      disabled={
                        visibleColumnsCount <= 1 && isColumnVisible(col)
                      }
                    ></ColumnCard>
                  )
              )}
            </CheckBoxesContainer>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <FooterActionsContainer className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button
                icon="reset"
                text={translateFunc("ColumnSelector_Reset")}
                onClick={resetColumnOrder}
              ></Button>
              <Button
                intent="success"
                icon="floppy-disk"
                text={translateFunc("ColumnSelector_Save")}
                onClick={saveColumnsState}
              ></Button>
            </FooterActionsContainer>
          </div>
        </Dialog>
      </Container>
    </DndProvider>
  );
};
